import { Theme, css } from '@emotion/react';
import { Skeleton } from '@mui/material';
import { Box, BoxProps } from '@mui/system';
import { Types, useTranslation } from '@scorenco/core';

import { ClubLogo, Score } from '../../components';
import { T_KEYS } from '../../translations';

export const style = {
  root: (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
    width: 100%;

    .event-card_container {
      display: flex;
      align-items: center;
      min-height: 3rem;
      flex: 1;
      justify-content: space-between;
    }

    .event-card_team-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 20%;
    }

    .event-card_header_text {
      text-align: center;
      margin-bottom: 1rem;
    }

    .event-card_footer {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 1rem;
      margin-left: 1rem;
      margin-right: 1rem;
      padding: 0 1.5rem;
      border-radius: 0.25rem;

      &.event-card_footer-live {
        position: relative;
        background-color: #cc062a15;

        &::before {
          position: absolute;
          left: 0.5rem;
          content: '';
          height: 0.5rem;
          width: 0.5rem;
          margin-right: 0.25rem;
          background-color: #cc062a;
          border-radius: 9999px;
          display: inline-block;
        }
      }
    }

    .event-card_footer_text {
      font-size: 0.75rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .event-card_logo {
      max-width: 3.75rem;
      height: 4.5rem;
      margin-bottom: 1rem;
    }

    .event_card_give-score {
      text-align: center;
      font-size: 10px;
      font-style: italic;
    }

    .event-card_scores {
      display: flex;
      margin: 0.25rem 0;
      font-weight: bold;
    }

    .event-card_date {
      text-align: center;
      line-height: 1.2;
      font-style: italic;
      padding: 1rem;
      background-color: #f3f5f9;
      border-radius: 0.25rem;

      .date {
        font-size: 1rem;
        line-height: 1.2;
        font-style: normal;
        font-weight: 700;
        text-transform: uppercase;
      }
      .time {
        font-size: 0.75rem;
      }
    }

    .event-card_team {
      flex: 1;
      line-height: 1.2;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding: 0 ${theme.spacing(2)};
      min-width: 10rem;
      text-align: center;
    }

    .event-card_team-away {
      text-align: center;
    }
  `,
};

export const normalizeEvent = (event?: Types.Competitions_Event_Detail) => {
  const date = event?.date && new Date(event.date);
  const time = event?.time && new Date(event.time);
  const isStarted =
    event?.teams[0]?.score !== null &&
    event?.teams[0]?.score !== undefined &&
    event?.teams[1]?.score !== null &&
    event?.teams[1]?.score !== undefined;

  if (event?.teams.length === 2) {
    return {
      teams: event?.teams.map((playingTeam: any) => ({
        name: playingTeam?.name_in_competition,
        logo: playingTeam?.logo,
        score: playingTeam?.score,
        short_name: playingTeam?.name_in_club,
      })),
      isStarted,
      isLive: isStarted && event?.status === 'live',
      status: event?.status,
      date: date?.toLocaleDateString([], {
        month: 'long',
        day: '2-digit',
      }),
      hour: time?.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      competition: {
        name: event?.level_name,
      },
    };
  } else {
    return {
      teams: [
        {
          name: '',
          logo: '',
          score: '',
          short_name: '',
        },
        {
          name: '',
          logo: '',
          score: '',
          short_name: '',
        },
      ],
      isStarted,
      isLive: isStarted && event?.status === 'live',
      status: 'to_come',
      date: date?.toLocaleDateString([], {
        month: 'long',
        day: '2-digit',
      }),
      hour: time?.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      competition: {
        name: '',
      },
    };
  }
};

export const EventCard = ({
  event,
  showCompetition = true,
  showTopDate = true,
  clubId,
  showShortName = false,
  ...rest
}: EventCardProps) => {
  const e = normalizeEvent(event);
  const { t } = useTranslation();

  let scoreboard;

  if (e.status === 'to_come') {
    (showTopDate = false),
      (scoreboard = (
        <div className="event-card_date">
          <div className="date">{e.date}</div>
          <div className="time">{e.hour}</div>
        </div>
      ));
  } else if (e.status === 'postponed') {
    scoreboard = (
      <div className="event_card_give-score">{t(T_KEYS.EVENT_POSTPONED)}</div>
    );
  } else if (e.status === 'withdrawal') {
    scoreboard = (
      <div className="event_card_give-score">{t(T_KEYS.EVENT_WITHDRAWAL)}</div>
    );
  } else if (e.status === 'live' && !e.isStarted) {
    scoreboard = (
      <div
        className="event_card_give-score"
        dangerouslySetInnerHTML={{
          __html: t(T_KEYS.EVENT_CARD_GIVE_SCORE) as string,
        }}
      />
    );
  } else {
    scoreboard = (
      <div className="event-card_scoreboard">
        <div className="event-card_scores">
          <Score
            score={e.teams?.[0]?.score}
            opponentScore={event?.teams?.[1]?.score}
            isWinner={
              e.status === 'finish' && e.teams[0]?.score > e.teams[1]?.score
            }
            sx={{ width: '3rem', height: '4rem', mr: '0.25rem' }}
          />
          <Score
            score={e.teams?.[1]?.score}
            opponentScore={event?.teams?.[1]?.score}
            isWinner={
              e.status === 'finish' && e.teams[0]?.score < e.teams[1]?.score
            }
            sx={{ width: '3rem', height: '4rem', ml: '0.25rem' }}
          />
        </div>
      </div>
    );
  }

  return (
    <Box css={style.root} {...rest}>
      {showTopDate && (
        <div className="event-card_header">
          <div className="event-card_header_text">
            <div>
              {e.date} - {e.hour}
            </div>
          </div>
        </div>
      )}

      <Box className="event-card_container">
        <Box className="event-card_team-container">
          <ClubLogo
            className="event-card_logo"
            logo={e.teams[0].logo}
            name={e.teams[0].name}
            shieldColor="home"
          />
          <div className="event-card_team">
            {event?.teams[0]?.club_id === clubId && showShortName
              ? e.teams[0].short_name || <Skeleton />
              : e.teams[0].name || <Skeleton />}
          </div>
        </Box>
        {scoreboard}
        <Box className="event-card_team-container">
          <ClubLogo
            className="event-card_logo"
            logo={e.teams[1].logo}
            name={e.teams[1].name}
            shieldColor="away"
          />

          <div className="event-card_team event-card_team-away">
            {event?.teams[1]?.club_id === clubId && showShortName
              ? e.teams[1].short_name || <Skeleton />
              : e.teams[1].name || <Skeleton />}
          </div>
        </Box>
      </Box>

      {showCompetition && (
        <div
          className={['event-card_footer', e.isLive && 'event-card_footer-live']
            .filter(Boolean)
            .join(' ')}
        >
          <div className="event-card_footer_text">
            {e.isLive && !e.competition.name
              ? t(T_KEYS.EVENT_LIVE)
              : e.competition.name}
          </div>
        </div>
      )}
    </Box>
  );
};

export type EventCardProps = BoxProps & {
  event?: Types.Competitions_Event_Detail;
  showCompetition?: boolean;
  showTopDate?: boolean;
  clubId?: number;
  showShortName?: boolean;
};
